import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotateRight,
  faSpinner,
  faCheck,
  faTimes,
  faRobot,
  faPen,
  faClipboardCheck,
  faImage,
  faTrash,
  faEye,
  faMicrophone,
  faArrowLeft,
  faFolderOpen,
  faSave,
  faStop,
  faPaperclip,
  faCamera
} from '@fortawesome/free-solid-svg-icons';
import { useApp } from '../contexts/AppContext';
import ConfirmDialog from './ConfirmDialog';
import ZoomableImage from './ZoomableImage';
import AnnotatableImage from './AnnotatableImage';
import TwoStepAiControls from './TwoStepAiControls';
import { isKnownLocalModel } from '../utils/localModels';
import ProviderSelect from './ProviderSelect';
import { CHECK_PROVIDER_KEY, readPageProvider, writePageProvider } from '../utils/pageProvider';
import useAiStatus from '../utils/useAiStatus';
import '../styles/RechecksTab.css';

const API_BASE_URL = window.API_BASE_URL;

const STATUS_META = {
  pending:  { label: 'Pending',  bg: 'rgba(245,158,11,0.15)', fg: '#B45309' },
  approved: { label: 'Approved', bg: 'rgba(16,185,129,0.15)', fg: '#047857' },
  rejected: { label: 'Rejected', bg: 'rgba(239,68,68,0.15)',  fg: '#B91C1C' }
};

const RESOLUTION_LABELS = {
  manual_check: 'Manual check',
  ai_recheck: 'AI re-check',
  marks_updated: 'Approved: you can update on edupage'
};

const TEACHER_RESOLUTION_HINTS = {
  marks_updated: 'Approved: you can update on edupage',
  manual_check: 'The examiner will re-check this paper manually.',
  ai_recheck: 'The examiner will re-check this paper with AI.'
};

const DEFAULT_STATUS_NOTES = new Set([
  'Approved: you can update on edupage',
  TEACHER_RESOLUTION_HINTS.manual_check,
  TEACHER_RESOLUTION_HINTS.ai_recheck,
]);

/** Real examiner-written note only — not the auto status phrase. */
const examinerExtraNote = (req) => {
  const note = (req?.review_note && String(req.review_note).trim()) || '';
  if (!note) return '';
  if (DEFAULT_STATUS_NOTES.has(note)) return '';
  return note;
};

const statusChipLabel = (req) => {
  if (req.status === 'approved' && req.resolution === 'marks_updated') {
    return 'Approved: you can update on edupage';
  }
  return STATUS_META[req.status]?.label || req.status;
};

export default function RechecksTab({ currentUser }) {
  const navigate = useNavigate();
  const { handleRecheckStudent, setMessage } = useApp();

  const role = currentUser?.role;
  // Teachers: All. Examiner / admin / management: Pending.
  const [statusFilter, setStatusFilter] = useState(null);
  // Default matches backend ORDER BY created_at DESC
  const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [teacherImages, setTeacherImages] = useState([]); // [{ downloadUrl, fileName }]
  const [teacherImageIdx, setTeacherImageIdx] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [reviewImageUrl, setReviewImageUrl] = useState(null);
  const [reviewImageLoading, setReviewImageLoading] = useState(false);
  const [reviewAudioUrl, setReviewAudioUrl] = useState(null);
  const [reviewAudioLoading, setReviewAudioLoading] = useState(false);
  const [imagePanel, setImagePanel] = useState('teacher'); // 'teacher' | 'examiner'
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [examinerAnnotator, setExaminerAnnotator] = useState(null); // { url, name }
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [micBusy, setMicBusy] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const streamRef = useRef(null);
  const reviewImageInputRef = useRef(null);

  // Approve flow state
  const [approveStep, setApproveStep] = useState(null); // null | 'choose' | 'ai_config'
  const [reviewNote, setReviewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // AI recheck settings (shared model keys and provider with the Checking tab)
  const [pageProvider, setPageProvider] = useState(() => readPageProvider(CHECK_PROVIDER_KEY));
  const isLocalProvider = pageProvider === 'local';
  const isGeminiProvider = pageProvider === 'gemini';
  const isMinimaxProvider = pageProvider === 'minimax';
  const changePageProvider = (value) => {
    setPageProvider(value);
    writePageProvider(CHECK_PROVIDER_KEY, value);
  };
  const aiStatus = useAiStatus(8000);
  const [ocrModel, setOcrModel] = useState(() => {
    const saved = localStorage.getItem('urduOcrModel');
    if (!isLocalProvider) return saved || 'gemini-2.5-flash';
    if (!isKnownLocalModel(saved)) return 'qwen38_27b';
    return saved;
  });
  const [gradingModel, setGradingModel] = useState(() => {
    const saved = localStorage.getItem('urduGradingModel');
    if (!isLocalProvider) return saved || 'gemini-2.5-pro';
    if (!isKnownLocalModel(saved)) return 'ggml';
    return saved;
  });
  const [ocrHost, setOcrHost] = useState(() => localStorage.getItem('ocrHost') || 'gpu');
  const [gradingHost, setGradingHost] = useState(() => localStorage.getItem('gradingHost') || 'mac');
  const [aiRecheckMode, setAiRecheckMode] = useState('full'); // full | ocr_only | grading_only

  useEffect(() => {
    if (pageProvider === 'local') {
      if (!isKnownLocalModel(ocrModel)) setOcrModel('qwen38_27b');
      if (!isKnownLocalModel(gradingModel)) setGradingModel('ggml');
    } else if (pageProvider === 'gemini') {
      if (typeof ocrModel !== 'string' || !ocrModel.startsWith('gemini-')) setOcrModel('gemini-3.1-pro-preview');
      if (typeof gradingModel !== 'string' || !gradingModel.startsWith('gemini-')) setGradingModel('gemini-3.1-pro-preview');
    } else if (pageProvider === 'minimax') {
      const ok = new Set(['', 'MiniMax-M2', 'MiniMax-M1', 'MiniMax-Text-01']);
      if (!ok.has(ocrModel)) setOcrModel('');
      if (!ok.has(gradingModel)) setGradingModel('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageProvider]);
  useEffect(() => {
    try { localStorage.setItem('urduOcrModel', ocrModel || ''); } catch {}
  }, [ocrModel]);
  useEffect(() => {
    try { localStorage.setItem('urduGradingModel', gradingModel || ''); } catch {}
  }, [gradingModel]);
  useEffect(() => {
    try { localStorage.setItem('ocrHost', ocrHost || ''); } catch {}
  }, [ocrHost]);
  useEffect(() => {
    try { localStorage.setItem('gradingHost', gradingHost || ''); } catch {}
  }, [gradingHost]);
  useEffect(() => {
    try { localStorage.setItem('recheckAiMode', aiRecheckMode || 'full'); } catch {}
  }, [aiRecheckMode]);

  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const selectAllRef = useRef(null);
  const lastSelectedIdRef = useRef(null);

  const canReview = currentUser?.role === 'examiner' || currentUser?.role === 'administrator';
  // Teachers may withdraw their own pending requests; examiners/admins may delete any.
  const canDeleteRequest = (req) => {
    if (!req) return false;
    if (canReview) return true;
    return role === 'teacher' && req.status === 'pending';
  };

  // Apply role-based default once currentUser is known (don't override later clicks).
  useEffect(() => {
    if (statusFilter != null || !role) return;
    setStatusFilter(role === 'teacher' ? 'all' : 'pending');
  }, [role, statusFilter]);

  const effectiveFilter = statusFilter ?? (role === 'teacher' ? 'all' : 'pending');

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/recheck-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to load re-check requests');
      }
      setRequests(data.requests || []);
    } catch (err) {
      console.error('Error loading recheck requests:', err);
      setError(err.message || 'Failed to load re-check requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const classSortKey = (r) => {
    const cls = [r.class, r.section].filter(Boolean).join('-');
    return cls + (r.subject ? ` · ${r.subject}` : '');
  };

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // Date defaults to newest-first; teacher/class to A→Z
      return { column, direction: column === 'date' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (column) => (
    <span className={`rechecks-sort-ind${sortConfig.column === column ? ' active' : ''}`}>
      {sortConfig.column === column ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  const filtered = useMemo(() => {
    const list =
      effectiveFilter === 'all'
        ? [...requests]
        : requests.filter((r) => r.status === effectiveFilter);

    if (!sortConfig.column) return list;

    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let cmp = 0;
      if (sortConfig.column === 'teacher') {
        cmp = String(a.requested_by || '').localeCompare(String(b.requested_by || ''), undefined, {
          sensitivity: 'base'
        });
      } else if (sortConfig.column === 'class') {
        cmp = classSortKey(a).localeCompare(classSortKey(b), undefined, {
          sensitivity: 'base',
          numeric: true
        });
      } else if (sortConfig.column === 'date') {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        cmp = aTime - bTime;
      }
      return cmp * dir;
    });
    return list;
  }, [requests, effectiveFilter, sortConfig]);

  const pendingCount = useMemo(
    () => requests.filter(r => r.status === 'pending').length,
    [requests]
  );

  const deletableIds = useMemo(
    () => filtered.filter((req) => canDeleteRequest(req)).map((req) => req.id),
    // canDeleteRequest only depends on role and canReview
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, role, canReview]
  );

  const selectedInView = useMemo(
    () => deletableIds.filter((id) => selectedIds.has(id)),
    [deletableIds, selectedIds]
  );

  const allSelected = deletableIds.length > 0 && selectedInView.length === deletableIds.length;
  const someSelected = selectedInView.length > 0 && !allSelected;
  const showSelection = deletableIds.length > 0;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  useEffect(() => {
    const allowed = new Set(deletableIds);
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (allowed.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [deletableIds]);

  const applyUpdatedRequest = (updated) => {
    if (!updated) return;
    setSelected(updated);
    setRequests(prev => prev.map(r => (r.id === updated.id ? updated : r)));
  };

  const fetchPresigned = async (path, setUrl, setLoading) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/recheck-requests/${path}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.downloadUrl) setUrl(data.downloadUrl);
      else setUrl(null);
    } catch (err) {
      console.error('Error loading attachment:', err);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  };

  const stopRecordingCleanup = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setRecording(false);
    setMicBusy(false);
    setRecordSecs(0);
    audioChunksRef.current = [];
  };

  const openDetail = async (req) => {
    stopRecordingCleanup();
    setSelected(req);
    setApproveStep(null);
    setReviewNote(examinerExtraNote(req));
    setAttachmentUrl(null);
    setAudioUrl(null);
    setReviewImageUrl(null);
    setReviewAudioUrl(null);
    setTeacherImages([]);
    setTeacherImageIdx(0);
    setImagePanel(req.review_image_key ? 'examiner' : 'teacher');
    setMediaError('');
    setError('');

    if (req.image_key || req.images_json) {
      (async () => {
        try {
          setAttachmentLoading(true);
          const token = localStorage.getItem('authToken');
          const res = await fetch(`${API_BASE_URL}/recheck-requests/${req.id}/images`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && Array.isArray(data.images) && data.images.length) {
            setTeacherImages(data.images);
            setAttachmentUrl(data.images[0].downloadUrl);
            setTeacherImageIdx(0);
          } else if (req.image_key) {
            await fetchPresigned(`${req.id}/image`, setAttachmentUrl, setAttachmentLoading);
          }
        } catch (err) {
          console.error('Error loading teacher images:', err);
        } finally {
          setAttachmentLoading(false);
        }
      })();
    }
    if (req.audio_key) {
      fetchPresigned(`${req.id}/audio`, setAudioUrl, setAudioLoading);
    }
    if (req.review_image_key) {
      fetchPresigned(`${req.id}/review-image`, setReviewImageUrl, setReviewImageLoading);
    }
    if (req.review_audio_key) {
      fetchPresigned(`${req.id}/review-audio`, setReviewAudioUrl, setReviewAudioLoading);
    }
  };

  const closeDetail = () => {
    stopRecordingCleanup();
    closeExaminerAnnotator();
    setSelected(null);
    setApproveStep(null);
    setAttachmentUrl(null);
    setAudioUrl(null);
    setReviewImageUrl(null);
    setReviewAudioUrl(null);
    setTeacherImages([]);
    setTeacherImageIdx(0);
    setReviewNote('');
    setMediaError('');
    setImagePanel('teacher');
  };

  const reviewRequest = async ({ status, resolution }) => {
    if (!selected) return;
    try {
      setSubmitting(true);
      const token = localStorage.getItem('authToken');
      const body = {
        status,
        review_note: reviewNote.trim() || undefined
      };
      if (status === 'approved') {
        body.resolution = resolution;
      }

      const res = await fetch(`${API_BASE_URL}/recheck-requests/${selected.id}/review`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to review request');
      }

      // AI path: hand images off to Evaluate → Sort and navigate there.
      if (status === 'approved' && resolution === 'ai_recheck' && selected.rubric_id) {
        const imageNames = (selected.student_images || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
        if (imageNames.length > 0 && handleRecheckStudent) {
          handleRecheckStudent(imageNames, `rubrics/${selected.rubric_id}`);
        }
        setMessage?.({
          show: true,
          text: `Approved — opening Evaluate to re-check ${selected.student_name} with AI.`,
          type: 'success'
        });
        closeDetail();
        await loadRequests();
        navigate('/evaluate/sort');
        return;
      }

      const wasReviewed = selected.status !== 'pending';
      let msg = status === 'rejected'
        ? (wasReviewed
          ? `Changed decision to Rejected for ${selected.student_name}.`
          : `Rejected re-check for ${selected.student_name}.`)
        : resolution === 'marks_updated'
          ? `Approved — teacher should update ${selected.student_name}'s marks on Edupage.`
          : `Approved — mark as manual check for ${selected.student_name}.`;

      setMessage?.({ show: true, text: msg, type: 'success' });
      closeDetail();
      await loadRequests();
    } catch (err) {
      console.error('Error reviewing request:', err);
      setError(err.message || 'Failed to review request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = () => {
    if (!selected) return;
    const changing = selected.status === 'approved';
    setConfirm({
      show: true,
      text: changing
        ? `Change the decision for ${selected.student_name} from Approved to Rejected? The teacher will get a new notice.`
        : `Reject the re-check request for ${selected.student_name}?`,
      onConfirm: () => reviewRequest({ status: 'rejected' })
    });
  };

  const saveReviewNote = async () => {
    if (!selected || !canReview) return;
    try {
      setSubmitting(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/recheck-requests/${selected.id}/note`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ review_note: reviewNote.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to save examiner note');
      }
      const updated = data.request;
      applyUpdatedRequest(updated);
      setReviewNote(examinerExtraNote(updated));
      setMessage?.({ show: true, text: 'Examiner note saved.', type: 'success' });
    } catch (err) {
      console.error('Error saving review note:', err);
      setError(err.message || 'Failed to save examiner note');
    } finally {
      setSubmitting(false);
    }
  };

  const uploadReviewImageFile = async (file) => {
    if (!selected || !canReview || !file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      setMediaError('Please choose an image file (JPEG, PNG, GIF, or WEBP).');
      return;
    }
    try {
      setMediaUploading(true);
      setMediaError('');
      const token = localStorage.getItem('authToken');
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE_URL}/recheck-requests/${selected.id}/review-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to upload examiner image');
      }
      applyUpdatedRequest(data.request);
      setImagePanel('examiner');
      await fetchPresigned(`${data.request.id}/review-image`, setReviewImageUrl, setReviewImageLoading);
      setMessage?.({ show: true, text: 'Examiner screenshot saved.', type: 'success' });
      return true;
    } catch (err) {
      console.error('Error uploading examiner image:', err);
      setMediaError(err.message || 'Failed to upload examiner image');
      return false;
    } finally {
      setMediaUploading(false);
    }
  };

  const closeExaminerAnnotator = () => {
    setExaminerAnnotator(prev => {
      if (prev?.url) {
        try { URL.revokeObjectURL(prev.url); } catch (_) { /* ignore */ }
      }
      return null;
    });
  };

  // Open only the currently selected teacher attachment in the annotator.
  // No surrounding recheck page UI is captured, so the examiner can mark the
  // disputed area and send that clean image back to the teacher.
  const openExaminerScreenshot = async () => {
    if (!selected || !canReview || screenshotLoading || mediaUploading) return;
    try {
      setScreenshotLoading(true);
      setMediaError('');
      const token = localStorage.getItem('authToken');
      const index = Math.max(0, teacherImageIdx || 0);
      const res = await fetch(
        `${API_BASE_URL}/recheck-requests/${selected.id}/image-raw?index=${index}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to load teacher image');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setExaminerAnnotator({
        url,
        name: teacherImages[index]?.fileName || selected.image_name || `recheck_${selected.id}.png`
      });
    } catch (err) {
      console.error('Error opening examiner screenshot annotator:', err);
      setMediaError(err.message || 'Failed to open screenshot annotator');
    } finally {
      setScreenshotLoading(false);
    }
  };

  const saveExaminerAnnotation = async (blob, fileName) => {
    if (!blob || !selected) return;
    const base = String(fileName || examinerAnnotator?.name || `recheck_${selected.id}`)
      .replace(/\.[^/.]+$/, '');
    const file = new File(
      [blob],
      `${base}_examiner_marked.png`,
      { type: 'image/png' }
    );
    const saved = await uploadReviewImageFile(file);
    if (saved) closeExaminerAnnotator();
  };

  const uploadReviewAudioFile = async (file) => {
    if (!selected || !canReview || !file) return;
    const ok = (file.type && file.type.startsWith('audio/'))
      || /\.(webm|ogg|mp3|wav|m4a|aac|mp4)$/i.test(file.name || '');
    if (!ok) {
      setMediaError('Please choose an audio file (webm, mp3, wav, m4a, ogg).');
      return;
    }
    try {
      setMediaUploading(true);
      setMediaError('');
      const token = localStorage.getItem('authToken');
      const form = new FormData();
      form.append('audio', file);
      const res = await fetch(`${API_BASE_URL}/recheck-requests/${selected.id}/review-audio`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to upload examiner voice note');
      }
      applyUpdatedRequest(data.request);
      await fetchPresigned(`${data.request.id}/review-audio`, setReviewAudioUrl, setReviewAudioLoading);
      setMessage?.({ show: true, text: 'Examiner voice note saved.', type: 'success' });
    } catch (err) {
      console.error('Error uploading examiner audio:', err);
      setMediaError(err.message || 'Failed to upload examiner voice note');
    } finally {
      setMediaUploading(false);
    }
  };

  const removeReviewImage = async () => {
    if (!selected || !canReview) return;
    try {
      setMediaUploading(true);
      setMediaError('');
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/recheck-requests/${selected.id}/review-image`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to remove examiner image');
      }
      applyUpdatedRequest(data.request);
      setReviewImageUrl(null);
      setImagePanel('teacher');
    } catch (err) {
      setMediaError(err.message || 'Failed to remove examiner image');
    } finally {
      setMediaUploading(false);
    }
  };

  const removeReviewAudio = async () => {
    if (!selected || !canReview) return;
    try {
      setMediaUploading(true);
      setMediaError('');
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/recheck-requests/${selected.id}/review-audio`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to remove examiner voice note');
      }
      applyUpdatedRequest(data.request);
      setReviewAudioUrl(null);
    } catch (err) {
      setMediaError(err.message || 'Failed to remove examiner voice note');
    } finally {
      setMediaUploading(false);
    }
  };

  const startRecording = async () => {
    setMediaError('');
    setMicBusy(true);
    try {
      if (!window.isSecureContext) {
        setMediaError('Microphone needs HTTPS (or localhost). Use "Upload audio" instead.');
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        setMediaError('This browser cannot record audio. Use "Upload audio" instead.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];
      let mimeType = '';
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mimeType = 'audio/webm;codecs=opus';
        else if (MediaRecorder.isTypeSupported('audio/webm')) mimeType = 'audio/webm';
        else if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
        else if (MediaRecorder.isTypeSupported('audio/ogg')) mimeType = 'audio/ogg';
      }
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data);
      };
      recorder.onerror = () => {
        setMediaError('Recording failed. Try again or upload an audio file.');
        stopRecordingCleanup();
      };
      recorder.onstop = async () => {
        const blobType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: blobType });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        if (recordTimerRef.current) {
          clearInterval(recordTimerRef.current);
          recordTimerRef.current = null;
        }
        setRecording(false);
        setMicBusy(false);
        if (!blob.size) {
          setMediaError('Recording was empty. Please try again.');
          return;
        }
        const ext = blobType.includes('mp4') ? 'm4a'
          : blobType.includes('ogg') ? 'ogg'
          : 'webm';
        const file = new File([blob], `examiner-voice-note.${ext}`, { type: blobType.split(';')[0] });
        await uploadReviewAudioFile(file);
      };
      recorder.start(250);
      setRecording(true);
      setRecordSecs(0);
      setMicBusy(false);
      recordTimerRef.current = setInterval(() => setRecordSecs(s => s + 1), 1000);
    } catch (err) {
      console.error('Microphone error:', err);
      const name = err && err.name;
      let msg = 'Could not access the microphone.';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        msg = 'Microphone permission denied. Allow mic access, or use "Upload audio".';
      } else if (name === 'NotFoundError') {
        msg = 'No microphone found. Use "Upload audio" instead.';
      }
      setMediaError(msg);
      stopRecordingCleanup();
    } finally {
      setMicBusy(false);
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try { recorder.requestData(); } catch (_) { /* optional */ }
      recorder.stop();
    }
  };

  useEffect(() => () => stopRecordingCleanup(), []);

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) deletableIds.forEach((id) => next.delete(id));
      else deletableIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleRowSelected = (id, shiftKey) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shiftKey && lastSelectedIdRef.current != null) {
        const a = deletableIds.indexOf(lastSelectedIdRef.current);
        const b = deletableIds.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [start, end] = a < b ? [a, b] : [b, a];
          const turningOn = !prev.has(id);
          for (let i = start; i <= end; i += 1) {
            if (turningOn) next.add(deletableIds[i]);
            else next.delete(deletableIds[i]);
          }
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    lastSelectedIdRef.current = id;
  };

  const handleDelete = (req) => {
    if (!canDeleteRequest(req)) return;

    const isTeacherWithdraw = role === 'teacher' && !canReview;
    setConfirm({
      show: true,
      text: isTeacherWithdraw
        ? `Withdraw the re-check request for ${req.student_name}? This cannot be undone.`
        : `Delete re-check request for ${req.student_name}?`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('authToken');
          const res = await fetch(`${API_BASE_URL}/recheck-requests/${req.id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) {
            throw new Error(data.message || 'Failed to delete request');
          }
          setMessage?.({
            show: true,
            text: isTeacherWithdraw
              ? `Withdrawn re-check request for ${req.student_name}.`
              : `Deleted re-check request for ${req.student_name}.`,
            type: 'success'
          });
          if (selected?.id === req.id) closeDetail();
          await loadRequests();
        } catch (err) {
          setError(err.message || 'Failed to delete request');
        }
      }
    });
  };

  const handleDeleteSelected = () => {
    const ids = selectedInView;
    if (!ids.length || bulkDeleting) return;

    const isTeacherWithdraw = role === 'teacher' && !canReview;
    const noun = ids.length === 1 ? 'request' : 'requests';
    setConfirm({
      show: true,
      text: isTeacherWithdraw
        ? `Withdraw ${ids.length} re-check ${noun}? This cannot be undone.`
        : `Delete ${ids.length} re-check ${noun}? This cannot be undone.`,
      onConfirm: async () => {
        try {
          setBulkDeleting(true);
          const token = localStorage.getItem('authToken');
          const res = await fetch(`${API_BASE_URL}/recheck-requests/bulk-delete`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ ids })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.success) {
            throw new Error(data.message || 'Failed to delete selected requests');
          }
          const deleted = Number(data.deleted) || ids.length;
          const failed = Array.isArray(data.failed) ? data.failed.length : 0;
          const verb = isTeacherWithdraw ? 'Withdrawn' : 'Deleted';
          setMessage?.({
            show: true,
            text: failed
              ? `${verb} ${deleted} re-check ${deleted === 1 ? 'request' : 'requests'}. ${failed} could not be removed.`
              : `${verb} ${deleted} re-check ${deleted === 1 ? 'request' : 'requests'}.`,
            type: failed ? 'warning' : 'success'
          });
          setSelectedIds(new Set());
          if (selected && ids.includes(selected.id)) closeDetail();
          await loadRequests();
        } catch (err) {
          setError(err.message || 'Failed to delete selected requests');
        } finally {
          setBulkDeleting(false);
        }
      }
    });
  };

  const formatDate = (val) => {
    if (!val) return '—';
    try {
      return new Date(val).toLocaleString();
    } catch {
      return String(val);
    }
  };

  const formatTestDate = (val) => {
    if (!val) return '—';
    try {
      const raw = typeof val === 'string' ? val.split('T')[0] : null;
      const d = raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)
        ? new Date(`${raw}T00:00:00`)
        : new Date(val);
      if (Number.isNaN(d.getTime())) return String(val);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return String(val);
    }
  };

  // Open the matching Scans folder (and student row when possible) in a new tab.
  // Query params are used so the new tab can pick them up (router state does not cross tabs).
  const openInScans = (req, e) => {
    if (e) e.stopPropagation();
    if (!req?.rubric_id) return;
    const params = new URLSearchParams();
    params.set('folder', String(req.rubric_id));
    if (req.result_data_id != null && req.result_data_id !== '') {
      params.set('resultDataId', String(req.result_data_id));
    }
    if (req.student_name) params.set('student', req.student_name);
    if (req.student_images) params.set('images', req.student_images);
    const url = `/scans?${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderExaminerAttachments = ({ editable }) => (
    <div className="rechecks-examiner-media">
      {mediaError && (
        <div className="rechecks-alert error" style={{ marginTop: 8 }} onClick={() => setMediaError('')}>
          {mediaError}
        </div>
      )}

      <div className="rechecks-examiner-media-block">
        <div className="rechecks-examiner-media-label">
          <FontAwesomeIcon icon={faImage} /> Screenshot
        </div>
        {editable && (
          <div className="rechecks-examiner-media-actions">
            <button
              type="button"
              className="rechecks-btn view"
              disabled={
                mediaUploading
                || recording
                || screenshotLoading
                || (!selected.image_key && !selected.images_json)
              }
              onClick={openExaminerScreenshot}
              title="Open only the current teacher image and mark it"
            >
              <FontAwesomeIcon
                icon={screenshotLoading ? faSpinner : faCamera}
                spin={screenshotLoading}
              />
              {screenshotLoading ? 'Opening…' : 'Screenshot'}
            </button>
            <button
              type="button"
              className="rechecks-btn view"
              disabled={mediaUploading || recording}
              onClick={() => reviewImageInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={mediaUploading ? faSpinner : faPaperclip} spin={mediaUploading} />
              {selected.review_image_key ? 'Replace image' : 'Upload image'}
            </button>
            <input
              ref={reviewImageInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (f) uploadReviewImageFile(f);
                e.target.value = '';
              }}
            />
            <div
              tabIndex={0}
              className="rechecks-paste-zone"
              title="Click here, then press Ctrl+V to paste a screenshot"
              onClick={(e) => e.currentTarget.focus()}
              onPaste={(e) => {
                const items = e.clipboardData?.items;
                if (!items) return;
                for (const item of items) {
                  if (item.type && item.type.startsWith('image/')) {
                    const file = item.getAsFile();
                    if (file) {
                      e.preventDefault();
                      uploadReviewImageFile(file);
                      return;
                    }
                  }
                }
              }}
            >
              Paste (Ctrl+V)
            </div>
            {selected.review_image_key && (
              <button
                type="button"
                className="rechecks-btn danger"
                disabled={mediaUploading}
                onClick={removeReviewImage}
                title="Remove examiner screenshot"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        )}
        {reviewImageLoading ? (
          <div className="rechecks-loading" style={{ padding: 12 }}>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading image...
          </div>
        ) : reviewImageUrl ? (
          <button
            type="button"
            className="rechecks-review-thumb"
            onClick={() => setImagePanel('examiner')}
            title="Show on the right panel"
          >
            <img src={reviewImageUrl} alt={selected.review_image_name || 'Examiner screenshot'} />
          </button>
        ) : (
          <p className="rechecks-muted" style={{ margin: '6px 0 0' }}>
            {editable ? 'Optional — upload or paste a screenshot for the teacher.' : 'No examiner screenshot.'}
          </p>
        )}
      </div>

      <div className="rechecks-examiner-media-block">
        <div className="rechecks-examiner-media-label">
          <FontAwesomeIcon icon={faMicrophone} /> Voice note
        </div>
        {editable && (
          <div className="rechecks-examiner-media-actions">
            {!recording ? (
              <button
                type="button"
                className="rechecks-btn approve"
                disabled={mediaUploading || micBusy}
                onClick={startRecording}
              >
                <FontAwesomeIcon icon={micBusy ? faSpinner : faMicrophone} spin={micBusy} />
                {micBusy ? 'Requesting mic…' : (selected.review_audio_key ? 'Re-record' : 'Record')}
              </button>
            ) : (
              <button type="button" className="rechecks-btn reject" onClick={stopRecording}>
                <FontAwesomeIcon icon={faStop} />
                Stop ({Math.floor(recordSecs / 60)}:{String(recordSecs % 60).padStart(2, '0')})
              </button>
            )}
            <label className={`rechecks-btn view ${mediaUploading || recording ? 'disabled' : ''}`}>
              <FontAwesomeIcon icon={faPaperclip} />
              Upload audio
              <input
                type="file"
                accept="audio/*,.webm,.mp3,.wav,.m4a,.ogg,.aac"
                disabled={mediaUploading || recording}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  if (f) uploadReviewAudioFile(f);
                  e.target.value = '';
                }}
              />
            </label>
            {selected.review_audio_key && !recording && (
              <button
                type="button"
                className="rechecks-btn danger"
                disabled={mediaUploading}
                onClick={removeReviewAudio}
                title="Remove examiner voice note"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        )}
        {recording && (
          <p className="rechecks-muted" style={{ marginTop: 6 }}>Recording… speak clearly, then press Stop.</p>
        )}
        {reviewAudioLoading ? (
          <div className="rechecks-loading" style={{ padding: 12 }}>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading audio...
          </div>
        ) : reviewAudioUrl && !recording ? (
          <audio controls src={reviewAudioUrl} style={{ width: '100%', marginTop: 8 }} />
        ) : !editable && !selected.review_audio_key ? (
          <p className="rechecks-muted" style={{ margin: '6px 0 0' }}>No examiner voice note.</p>
        ) : null}
      </div>
    </div>
  );

  // ── Full-page detail: left info + right big image ─────────────────────
  if (selected) {
    const statusChip = STATUS_META[selected.status] || { label: selected.status, bg: '#eee', fg: '#555' };
    const showingExaminerImage = imagePanel === 'examiner' && !!reviewImageUrl;
    const activeTeacherUrl = teacherImages[teacherImageIdx]?.downloadUrl || attachmentUrl;
    const activeImageUrl = showingExaminerImage ? reviewImageUrl : activeTeacherUrl;
    const activeImageLoading = showingExaminerImage ? reviewImageLoading : attachmentLoading;
    const activeImageName = showingExaminerImage
      ? (selected.review_image_name || 'Examiner screenshot')
      : (teacherImages[teacherImageIdx]?.fileName || selected.image_name || 'Problem image');

    return (
      <div className="rechecks-detail-page">
        <div className="rechecks-detail-left">
          <div className="rechecks-detail-left-header">
            <button type="button" className="rechecks-back-btn" onClick={closeDetail}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to list
            </button>
            {selected.rubric_id ? (
              <button
                type="button"
                className="rechecks-student-link"
                onClick={(e) => openInScans(selected, e)}
                title="Open this student in Scans"
              >
                <FontAwesomeIcon icon={faFolderOpen} />
                {selected.student_name}
              </button>
            ) : (
              <h2>
                <FontAwesomeIcon icon={faRotateRight} style={{ color: '#D97706' }} />
                {selected.student_name}
              </h2>
            )}
            <div className="rechecks-detail-header-status">
              <span
                className="rechecks-status-chip"
                style={{ background: statusChip.bg, color: statusChip.fg }}
              >
                {statusChipLabel(selected)}
              </span>
              {canDeleteRequest(selected) && (
                <button
                  type="button"
                  className="rechecks-btn danger"
                  onClick={() => handleDelete(selected)}
                  title={role === 'teacher' && !canReview ? 'Withdraw this request' : 'Delete this request'}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  {role === 'teacher' && !canReview ? 'Withdraw' : 'Delete'}
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="rechecks-alert error" onClick={() => setError('')}>
              {error}
            </div>
          )}

          <div className="rechecks-detail-scroll">
            <div className="rechecks-meta-grid rechecks-meta-grid--detail">
              <div><span>Marks</span><strong>{selected.original_marks ?? '—'}</strong></div>
              <div><span>Class</span><strong>{[selected.class, selected.section].filter(Boolean).join('-') || '—'}</strong></div>
              <div><span>Subject</span><strong>{selected.subject || '—'}</strong></div>
              <div><span>Test date</span><strong>{formatTestDate(selected.test_date)}</strong></div>
              <div><span>Requested by</span><strong>{selected.requested_by}</strong></div>
              <div><span>Submitted</span><strong>{formatDate(selected.created_at)}</strong></div>
            </div>

            <div className="rechecks-section">
              <h4>Teacher's explanation</h4>
              {selected.explanation ? (
                <p className="rechecks-explanation">{selected.explanation}</p>
              ) : (
                <p className="rechecks-muted">No typed explanation — see voice note below.</p>
              )}
            </div>

            {selected.audio_key && (
              <div className="rechecks-section">
                <h4>
                  <FontAwesomeIcon icon={faMicrophone} style={{ marginRight: 6, color: '#D97706' }} />
                  Voice note
                </h4>
                {audioLoading ? (
                  <div className="rechecks-loading" style={{ padding: 16 }}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading audio...
                  </div>
                ) : audioUrl ? (
                  <audio controls src={audioUrl} style={{ width: '100%', marginTop: 4 }} />
                ) : (
                  <p className="rechecks-muted">Could not load voice note.</p>
                )}
              </div>
            )}

            {selected.status !== 'pending' && (
              <div className="rechecks-section">
                <h4>Review result</h4>
                <p className="rechecks-muted">
                  {selected.status === 'approved' && selected.resolution && (
                    <>Resolution: <strong>{RESOLUTION_LABELS[selected.resolution] || selected.resolution}</strong><br /></>
                  )}
                  Reviewed by {selected.reviewed_by || '—'} on {formatDate(selected.reviewed_at)}
                </p>
                {selected.status === 'approved' && selected.resolution === 'marks_updated' && (
                  <div className="rechecks-edupage-banner">
                    {TEACHER_RESOLUTION_HINTS.marks_updated}
                  </div>
                )}
                {canReview ? (
                  <div style={{ marginTop: 10 }}>
                    <h4 style={{ marginBottom: 6 }}>Examiner&apos;s note</h4>
                    <textarea
                      className="rechecks-note-input"
                      rows={3}
                      value={reviewNote}
                      onChange={e => setReviewNote(e.target.value)}
                      placeholder="Note shown to the teacher..."
                    />
                    {renderExaminerAttachments({ editable: true })}
                    <div className="rechecks-review-actions" style={{ marginTop: 8 }}>
                      <button
                        className="rechecks-btn approve"
                        disabled={submitting || reviewNote.trim() === examinerExtraNote(selected)}
                        onClick={saveReviewNote}
                      >
                        <FontAwesomeIcon icon={submitting ? faSpinner : faSave} spin={submitting} />
                        Save note
                      </button>
                    </div>

                    {/* Allow reversing a mistaken approve/reject */}
                    <div style={{ marginTop: 14 }}>
                      <h4 style={{ marginBottom: 6 }}>Change decision</h4>
                      {selected.status === 'approved' && approveStep === null && (
                        <div className="rechecks-review-actions">
                          <button
                            className="rechecks-btn reject"
                            disabled={submitting}
                            onClick={handleReject}
                          >
                            <FontAwesomeIcon icon={faTimes} /> Change to Reject
                          </button>
                        </div>
                      )}
                      {selected.status === 'rejected' && approveStep === null && (
                        <div className="rechecks-review-actions">
                          <button
                            className="rechecks-btn approve"
                            disabled={submitting}
                            onClick={() => setApproveStep('choose')}
                          >
                            <FontAwesomeIcon icon={faCheck} /> Change to Approve
                          </button>
                        </div>
                      )}
                      {selected.status === 'rejected' && approveStep === 'choose' && (
                        <div className="rechecks-approve-options">
                          <p className="rechecks-choose-label">How do you want to resolve this?</p>
                          <button
                            className="rechecks-option-card"
                            disabled={submitting}
                            onClick={() => reviewRequest({ status: 'approved', resolution: 'manual_check' })}
                          >
                            <FontAwesomeIcon icon={faClipboardCheck} />
                            <div>
                              <strong>Check manually</strong>
                              <span>I will re-grade this paper myself</span>
                            </div>
                          </button>
                          <button
                            className="rechecks-option-card"
                            disabled={submitting}
                            onClick={() => setApproveStep('ai_config')}
                          >
                            <FontAwesomeIcon icon={faRobot} />
                            <div>
                              <strong>Check with AI</strong>
                              <span>Pick OCR/grading models &amp; GPU/Mac, then open Evaluate</span>
                            </div>
                          </button>
                          <button
                            className="rechecks-option-card"
                            disabled={submitting}
                            onClick={() => reviewRequest({ status: 'approved', resolution: 'marks_updated' })}
                          >
                            <FontAwesomeIcon icon={faPen} />
                            <div>
                              <strong>Update marks on Edupage</strong>
                              <span>Tell teacher to change marks in Edupage (not FastGrader)</span>
                            </div>
                          </button>
                          <button
                            className="rechecks-btn ghost"
                            disabled={submitting}
                            onClick={() => setApproveStep(null)}
                          >
                            Back
                          </button>
                        </div>
                      )}
                      {selected.status === 'rejected' && approveStep === 'ai_config' && (
                        <div className="rechecks-approve-options">
                          <p className="rechecks-choose-label">AI re-check settings</p>
                          <ProviderSelect value={pageProvider} onChange={changePageProvider} />
                          <TwoStepAiControls
                            title="OCR / grading models & hosts"
                            ocrModel={ocrModel}
                            setOcrModel={setOcrModel}
                            gradingModel={gradingModel}
                            setGradingModel={setGradingModel}
                            ocrHost={ocrHost}
                            setOcrHost={setOcrHost}
                            gradingHost={gradingHost}
                            setGradingHost={setGradingHost}
                            hosts={aiStatus?.local?.hosts || []}
                            isLocalProvider={isLocalProvider}
                            isGeminiProvider={isGeminiProvider}
                            isMinimaxProvider={isMinimaxProvider}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#6D28D9' }}>Recheck mode</span>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {[
                                { id: 'full', label: 'Full (OCR + grade)' },
                                { id: 'ocr_only', label: 'Re-OCR only' },
                                { id: 'grading_only', label: 'Re-grade only' }
                              ].map((m) => (
                                <button
                                  key={m.id}
                                  type="button"
                                  className={`rechecks-btn ${aiRecheckMode === m.id ? 'approve' : 'view'}`}
                                  onClick={() => setAiRecheckMode(m.id)}
                                  style={{ fontSize: 12 }}
                                >
                                  {m.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="rechecks-review-actions" style={{ marginTop: 12 }}>
                            <button
                              className="rechecks-btn approve"
                              disabled={submitting}
                              onClick={() => reviewRequest({ status: 'approved', resolution: 'ai_recheck' })}
                            >
                              <FontAwesomeIcon icon={submitting ? faSpinner : faRobot} spin={!!submitting} />
                              {submitting ? 'Starting…' : 'Start AI re-check'}
                            </button>
                            <button
                              className="rechecks-btn ghost"
                              disabled={submitting}
                              onClick={() => setApproveStep('choose')}
                            >
                              Back
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {examinerExtraNote(selected) && (
                      <p className="rechecks-examiner-note" style={{ marginTop: 8 }}>
                        Note: {examinerExtraNote(selected)}
                      </p>
                    )}
                    {(selected.review_image_key || selected.review_audio_key) && (
                      renderExaminerAttachments({ editable: false })
                    )}
                  </>
                )}
              </div>
            )}

            {canReview && selected.status === 'pending' && (
              <div className="rechecks-section">
                <h4>Reviewer note (optional)</h4>
                <textarea
                  className="rechecks-note-input"
                  rows={2}
                  value={reviewNote}
                  onChange={e => setReviewNote(e.target.value)}
                  placeholder="Optional note for the teacher..."
                />
                {renderExaminerAttachments({ editable: true })}

                {approveStep === null && (
                  <div className="rechecks-review-actions">
                    <button
                      className="rechecks-btn approve"
                      disabled={submitting}
                      onClick={() => setApproveStep('choose')}
                    >
                      <FontAwesomeIcon icon={faCheck} /> Approve
                    </button>
                    <button
                      className="rechecks-btn reject"
                      disabled={submitting}
                      onClick={handleReject}
                    >
                      <FontAwesomeIcon icon={faTimes} /> Reject
                    </button>
                  </div>
                )}

                {approveStep === 'choose' && (
                  <div className="rechecks-approve-options">
                    <p className="rechecks-choose-label">How do you want to resolve this?</p>
                    <button
                      className="rechecks-option-card"
                      disabled={submitting}
                      onClick={() => reviewRequest({ status: 'approved', resolution: 'manual_check' })}
                    >
                      <FontAwesomeIcon icon={faClipboardCheck} />
                      <div>
                        <strong>Check manually</strong>
                        <span>I will re-grade this paper myself</span>
                      </div>
                    </button>
                    <button
                      className="rechecks-option-card"
                      disabled={submitting}
                      onClick={() => setApproveStep('ai_config')}
                    >
                      <FontAwesomeIcon icon={faRobot} />
                      <div>
                        <strong>Check with AI</strong>
                        <span>Pick OCR/grading models &amp; GPU/Mac, then open Evaluate</span>
                      </div>
                    </button>
                    <button
                      className="rechecks-option-card"
                      disabled={submitting}
                      onClick={() => reviewRequest({ status: 'approved', resolution: 'marks_updated' })}
                    >
                      <FontAwesomeIcon icon={faPen} />
                      <div>
                        <strong>Update marks on Edupage</strong>
                        <span>Tell teacher to change marks in Edupage (not FastGrader)</span>
                      </div>
                    </button>
                    <button
                      className="rechecks-btn ghost"
                      disabled={submitting}
                      onClick={() => setApproveStep(null)}
                    >
                      Back
                    </button>
                  </div>
                )}

                {approveStep === 'ai_config' && (
                  <div className="rechecks-approve-options">
                    <p className="rechecks-choose-label">AI re-check settings</p>
                    <ProviderSelect value={pageProvider} onChange={changePageProvider} />
                    <TwoStepAiControls
                      title="OCR / grading models & hosts"
                      ocrModel={ocrModel}
                      setOcrModel={setOcrModel}
                      gradingModel={gradingModel}
                      setGradingModel={setGradingModel}
                      ocrHost={ocrHost}
                      setOcrHost={setOcrHost}
                      gradingHost={gradingHost}
                      setGradingHost={setGradingHost}
                      hosts={aiStatus?.local?.hosts || []}
                      isLocalProvider={isLocalProvider}
                      isGeminiProvider={isGeminiProvider}
                      isMinimaxProvider={isMinimaxProvider}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#6D28D9' }}>Recheck mode</span>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {[
                          { id: 'full', label: 'Full (OCR + grade)' },
                          { id: 'ocr_only', label: 'Re-OCR only' },
                          { id: 'grading_only', label: 'Re-grade only' }
                        ].map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            className={`rechecks-btn ${aiRecheckMode === m.id ? 'approve' : 'view'}`}
                            onClick={() => setAiRecheckMode(m.id)}
                            style={{ fontSize: 12 }}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="rechecks-review-actions" style={{ marginTop: 12 }}>
                      <button
                        className="rechecks-btn approve"
                        disabled={submitting}
                        onClick={() => reviewRequest({ status: 'approved', resolution: 'ai_recheck' })}
                      >
                        <FontAwesomeIcon icon={submitting ? faSpinner : faRobot} spin={!!submitting} />
                        {submitting ? 'Starting…' : 'Start AI re-check'}
                      </button>
                      <button
                        className="rechecks-btn ghost"
                        disabled={submitting}
                        onClick={() => setApproveStep('choose')}
                      >
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="rechecks-detail-right">
          <div className="rechecks-detail-right-header">
            <FontAwesomeIcon icon={faImage} style={{ color: '#2563EB' }} />
            <span>{activeImageName}</span>
            {(selected.image_key || selected.images_json || selected.review_image_key) && (
              <div className="rechecks-image-toggle">
                {(selected.image_key || selected.images_json) && (
                  <button
                    type="button"
                    className={imagePanel === 'teacher' || !reviewImageUrl ? 'active' : ''}
                    onClick={() => setImagePanel('teacher')}
                  >
                    Teacher{teacherImages.length > 1 ? ` (${teacherImages.length})` : ''}
                  </button>
                )}
                {selected.review_image_key && (
                  <button
                    type="button"
                    className={showingExaminerImage ? 'active' : ''}
                    onClick={() => setImagePanel('examiner')}
                  >
                    Examiner
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="rechecks-detail-image-stage">
            {activeImageLoading ? (
              <div className="rechecks-loading">
                <FontAwesomeIcon icon={faSpinner} spin /> Loading image...
              </div>
            ) : activeImageUrl ? (
              <>
                <ZoomableImage
                  url={activeImageUrl}
                  alt={activeImageName}
                  width="100%"
                  height="100%"
                  background="#111827"
                />
                {!showingExaminerImage && teacherImages.length > 1 && (
                  <div className="rechecks-teacher-nav">
                    <button
                      type="button"
                      disabled={teacherImageIdx <= 0}
                      onClick={() => {
                        const next = Math.max(0, teacherImageIdx - 1);
                        setTeacherImageIdx(next);
                        setAttachmentUrl(teacherImages[next]?.downloadUrl || null);
                      }}
                    >
                      ‹
                    </button>
                    <span>{teacherImageIdx + 1} / {teacherImages.length}</span>
                    <button
                      type="button"
                      disabled={teacherImageIdx >= teacherImages.length - 1}
                      onClick={() => {
                        const next = Math.min(teacherImages.length - 1, teacherImageIdx + 1);
                        setTeacherImageIdx(next);
                        setAttachmentUrl(teacherImages[next]?.downloadUrl || null);
                      }}
                    >
                      ›
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="rechecks-empty" style={{ color: '#e5e7eb' }}>
                {showingExaminerImage
                  ? 'Could not load examiner image.'
                  : (selected.image_key || selected.images_json ? 'Could not load image.' : 'No image attached to this request.')}
              </div>
            )}
          </div>
        </div>

        {examinerAnnotator && (
          <div
            data-html2canvas-ignore="true"
            className="app-annotator-overlay"
            style={{ background: 'rgba(0,0,0,0.88)' }}
          >
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <AnnotatableImage
                url={examinerAnnotator.url}
                alt={examinerAnnotator.name}
                width="100%"
                height="100%"
                background="#0a0a0a"
                onSaveForRecheck={saveExaminerAnnotation}
                saveButtonLabel="Save"
              />
              <button
                type="button"
                onClick={closeExaminerAnnotator}
                title="Close annotator"
                aria-label="Close annotator"
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 20,
                  width: 38,
                  height: 38,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  background: 'rgba(220,38,38,0.95)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.9)',
                  borderRadius: '50%',
                  boxShadow: '0 3px 12px rgba(0,0,0,0.45)',
                  cursor: 'pointer',
                  fontSize: 18
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        )}

        <ConfirmDialog
          confirm={confirm}
          onConfirm={() => {
            const fn = confirm.onConfirm;
            setConfirm({ show: false, text: '', onConfirm: null });
            if (fn) fn();
          }}
          onCancel={() => setConfirm({ show: false, text: '', onConfirm: null })}
        />
      </div>
    );
  }

  return (
    <div className="rechecks-tab">
      <div className="rechecks-header">
        <div>
          <h2>
            <FontAwesomeIcon icon={faRotateRight} style={{ marginRight: 10, color: '#D97706' }} />
            Re-check Requests
            {pendingCount > 0 && (
              <span className="rechecks-pending-badge">{pendingCount} pending</span>
            )}
          </h2>
          <p>
            {canReview
              ? 'Review teacher requests to re-check a student\'s paper. Approve with AI, manual check, or update marks directly.'
              : 'Track the status of your re-check requests. You can withdraw a pending request before it is reviewed.'}
          </p>
        </div>
        <div className="rechecks-header-actions">
          {selectedInView.length > 0 && (
            <button
              type="button"
              className="rechecks-btn reject"
              onClick={handleDeleteSelected}
              disabled={bulkDeleting}
            >
              <FontAwesomeIcon icon={bulkDeleting ? faSpinner : faTrash} spin={bulkDeleting} />
              {role === 'teacher' && !canReview ? 'Withdraw' : 'Delete'} selected ({selectedInView.length})
            </button>
          )}
          <div className="rechecks-filters">
            {['pending', 'approved', 'rejected', 'all'].map(s => (
              <button
                key={s}
                className={`rechecks-filter-btn ${effectiveFilter === s ? 'active' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'all' ? 'All' : STATUS_META[s].label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rechecks-alert error" onClick={() => setError('')}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="rechecks-loading">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading requests...
        </div>
      ) : filtered.length === 0 ? (
        <div className="rechecks-empty">
          No {effectiveFilter === 'all' ? '' : effectiveFilter + ' '}re-check requests.
        </div>
      ) : (
        <div className="rechecks-table-wrap">
          <table className="rechecks-table">
            <thead>
              <tr>
                {showSelection && (
                  <th className="rechecks-select-col">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label={allSelected ? 'Deselect all' : 'Select all'}
                      title={allSelected ? 'Deselect all' : 'Select all'}
                    />
                  </th>
                )}
                <th>Student</th>
                <th
                  className="rechecks-th-sortable"
                  onClick={() => handleSort('class')}
                  title="Sort by class / subject"
                >
                  Class / Subject{sortIndicator('class')}
                </th>
                <th>Marks</th>
                <th>Test date</th>
                <th
                  className="rechecks-th-sortable"
                  onClick={() => handleSort('teacher')}
                  title="Sort by teacher"
                >
                  Requested by{sortIndicator('teacher')}
                </th>
                <th>Status</th>
                <th>Examiner&apos;s note</th>
                <th
                  className="rechecks-th-sortable"
                  onClick={() => handleSort('date')}
                  title="Sort by date"
                >
                  Date{sortIndicator('date')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(req => {
                const chip = STATUS_META[req.status] || { label: req.status, bg: '#eee', fg: '#555' };
                const examinerNote = examinerExtraNote(req);
                const deletable = canDeleteRequest(req);
                const isSelected = selectedIds.has(req.id);
                return (
                  <tr
                    key={req.id}
                    className={[
                      isSelected ? 'is-selected' : '',
                      deletable ? 'is-selectable' : ''
                    ].filter(Boolean).join(' ') || undefined}
                    onClick={(e) => {
                      if (!deletable) return;
                      if (e.target.closest('button, a, input, label')) return;
                      toggleRowSelected(req.id, e.shiftKey);
                    }}
                  >
                    {showSelection && (
                      <td className="rechecks-select-col">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!deletable}
                          aria-label={`Select ${req.student_name}`}
                          onMouseDown={(e) => { lastSelectedIdRef.shiftKey = e.shiftKey; }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => {
                            if (!deletable) return;
                            toggleRowSelected(req.id, !!lastSelectedIdRef.shiftKey);
                          }}
                        />
                      </td>
                    )}
                    <td>
                      <div className="rechecks-student-cell">
                        {req.rubric_id ? (
                          <button
                            type="button"
                            className="rechecks-student-link rechecks-student-link--inline"
                            onClick={(e) => openInScans(req, e)}
                            title="Open this student in Scans"
                          >
                            <FontAwesomeIcon icon={faFolderOpen} />
                            {req.student_name}
                          </button>
                        ) : (
                          <strong>{req.student_name}</strong>
                        )}
                        {req.image_key && (
                          <span className="rechecks-has-image" title="Has attached image">
                            <FontAwesomeIcon icon={faImage} />
                          </span>
                        )}
                        {req.audio_key && (
                          <span className="rechecks-has-image" title="Has voice note" style={{ color: '#D97706' }}>
                            <FontAwesomeIcon icon={faMicrophone} />
                          </span>
                        )}
                        {req.review_image_key && (
                          <span className="rechecks-has-image" title="Examiner screenshot" style={{ color: '#2563EB' }}>
                            <FontAwesomeIcon icon={faImage} />
                          </span>
                        )}
                        {req.review_audio_key && (
                          <span className="rechecks-has-image" title="Examiner voice note" style={{ color: '#7C3AED' }}>
                            <FontAwesomeIcon icon={faMicrophone} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {[req.class, req.section].filter(Boolean).join('-') || '—'}
                      {req.subject ? ` · ${req.subject}` : ''}
                    </td>
                    <td>{req.original_marks != null && req.original_marks !== '' ? req.original_marks : '—'}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{formatTestDate(req.test_date)}</td>
                    <td>{req.requested_by}</td>
                    <td>
                      <span className="rechecks-status-chip" style={{ background: chip.bg, color: chip.fg }}>
                        {statusChipLabel(req)}
                        {req.status === 'approved' && req.resolution && req.resolution !== 'marks_updated' && (
                          <span className="rechecks-resolution"> · {RESOLUTION_LABELS[req.resolution] || req.resolution}</span>
                        )}
                      </span>
                    </td>
                    <td className="rechecks-note-cell">
                      {examinerNote ? (
                        <span className="rechecks-note-preview rechecks-examiner-note" title={examinerNote}>
                          {examinerNote}
                        </span>
                      ) : (
                        <span className="rechecks-muted">—</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{formatDate(req.created_at)}</td>
                    <td>
                      <div className="rechecks-row-actions">
                        <button
                          className="rechecks-btn view"
                          onClick={() => openDetail(req)}
                          title="View details"
                        >
                          <FontAwesomeIcon icon={faEye} /> View
                        </button>
                        {canDeleteRequest(req) && (
                          <button
                            className="rechecks-btn danger"
                            onClick={() => handleDelete(req)}
                            title={role === 'teacher' && !canReview ? 'Withdraw request' : 'Delete'}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        confirm={confirm}
        onConfirm={() => {
          const fn = confirm.onConfirm;
          setConfirm({ show: false, text: '', onConfirm: null });
          if (fn) fn();
        }}
        onCancel={() => setConfirm({ show: false, text: '', onConfirm: null })}
      />
    </div>
  );
}
